import { api, ApiRequestError } from "./api.js";
import { setDateFormat } from "./dates.js";
import { query } from "./dom.js";
import { initFormDialog } from "./form-dialog.js";
import { endedToast, entryTitle, runningEntry } from "./machine/actions.js";
import { changedFirewall, keepFirewallDetail, pendingSeconds } from "./machine/protection-model.js";
import { initActionsSection } from "./machine/actions-section.js";
import { initBackupsSection } from "./machine/backups-section.js";
import { failureText, machineApi, type Alert } from "./machine/api.js";
import { initConsole } from "./machine/console.js";
import { initDomainsSection } from "./machine/domains-section.js";
import { initInstancesSection } from "./machine/instances-section.js";
import { createJobWatch, pauseWhileHidden, POLL_MS } from "./machine/job-watch.js";
import { initLogsSection } from "./machine/logs-section.js";
import { initOutputDialog } from "./machine/output-dialog.js";
import { alertCounts } from "./machine/overview-model.js";
import { createUpdatesFeed } from "./machine/updates-feed.js";
import { type UpdatesWaiting, updatesWaiting } from "./machine/updates-model.js";
import { initOverviewSection } from "./machine/overview-section.js";
import { initProtectionSection } from "./machine/protection-section.js";
import { initRuleDialog } from "./machine/rule-dialog.js";
import { initSessionCard, type SessionCard } from "./machine/session-card.js";
import { initTargetDialog } from "./machine/target-dialog.js";
import { initSecuritySection } from "./machine/security-section.js";
import { initServicesSection } from "./machine/services-section.js";
import { initUpdatesSection } from "./machine/updates-section.js";
import { initAccountSection, type AccountSection } from "./manage/account-section.js";
import { serverNow } from "./server-clock.js";
import { takeServerZone } from "./machine/server-zone.js";
import { initThemeChoice } from "./theme.js";
import { showError, showToast, type ToastHandle } from "./toast.js";
import { initTooltips } from "./tooltip.js";

/**
 * elpresso machine's console: the page behind `machine.html`. It is the manage panel of
 * the builder as a page of its own, served by the machine binary on its own host with
 * its own login, so nothing of the builder and nothing an author wrote can reach it.
 */
const root = query("[data-console]");
const panel = query("[data-manage]", root);
const failed = query("[data-console-failed]", panel);
const failedLine = query("[data-console-failed-line]", failed);
query("[data-console-retry]", failed).addEventListener("click", () => location.reload());

/** How often the console asks for the alerts, as often as the machine looks. */
/** The account section with its Session card read whenever the section is shown. */
function withSession(section: AccountSection, session: SessionCard): AccountSection {
  return {
    ...section,
    show: () => {
      section.show();
      session.load();
    },
  };
}

const ALERTS_EVERY = 60_000;

/** The sections an alert can be about, in the nav's order. */
const SECTIONS = ["overview", "services", "logs", "instances", "updates", "protection", "security", "domains", "backups", "actions"] as const;

// The server's time zone and the format are asked with the login: every time a section shows
// is in them, so they must be known before the first one draws. Without an answer the times
// are the browser's, in the default format.
const startOverview = machineApi.overview().then((overview) => {
  setDateFormat({ date: overview.host.dateFormat ?? "", time: overview.host.timeFormat ?? "" });
  return takeServerZone(overview.host.timeZone);
});
void Promise.all([api.me(), startOverview.catch(() => false)])
  .then(() => {
    initTooltips();
    initFormDialog(query<HTMLDialogElement>("[data-form-dialog]"));
    // One watch follows the actions that run, for every section and the nav alike.
    const watch = createJobWatch({
      fetch: (id, since) => machineApi.entry(id, since),
      pause: pauseWhileHidden(POLL_MS),
      giveUp: (error) => {
        if (!(error instanceof ApiRequestError) || error.unreachable) return false;
        if (error.unauthorized) location.replace("/login");
        return true;
      },
    });
    const output = initOutputDialog(query<HTMLDialogElement>("[data-output-dialog]"), watch);
    const ruleDialog = initRuleDialog(query<HTMLDialogElement>("[data-rule-dialog]"));
    const targetDialog = initTargetDialog(query<HTMLDialogElement>("[data-target-dialog]"));
    // Every tab counts the alerts of its section, and Activity the job that runs: a number
    // in the nav is all the console says about an alert; the section itself shows what.
    // Updates counts what waits there too, since an update is no alert before a week.
    let counts = new Map<string, number>();
    let waiting: UpdatesWaiting = { count: 0, title: "" };
    const drawBadges = () => {
      const running = watch.running();
      for (const id of SECTIONS) {
        const count = counts.get(id) ?? 0;
        if (id === "actions" && running !== null) machine.setBadge(id, "1", entryTitle(running));
        else if (id === "updates" && waiting.count >= count && waiting.count > 0) machine.setBadge(id, String(waiting.count), waiting.title);
        else machine.setBadge(id, count === 0 ? "" : String(count), count === 1 ? "1 thing needs a look" : `${count} things need a look`);
      }
    };
    const showAlertCounts = (alerts: readonly Alert[]) => {
      counts = alertCounts(alerts);
      drawBadges();
    };
    const section = (id: string) => query(`[data-manage-section="${id}"]`, panel);
    initThemeChoice(query<HTMLSelectElement>("[data-theme-choice]", section("account")));
    // The nav's number on Updates and the overview's cards read one look at the updates.
    const updatesFeed = createUpdatesFeed({ updates: () => machineApi.updates(), packages: () => machineApi.packages() });
    const logs = initLogsSection(section("logs"));
    // "View log" of a service opens the logs on that unit; the console is there by the time it is clicked.
    const services = initServicesSection(section("services"), watch, (unit) => {
      machine.show("logs");
      logs.open(unit);
    });
    const protection = initProtectionSection(section("protection"), watch, ruleDialog);
    const machine = initConsole(
      panel,
      // Four categories in the colours of the builder's (#103): the machine itself, the
      // websites it serves, what keeps it safe, and the console.
      [
        {
          id: "machine",
          label: "Machine",
          icon: "server",
          color: "green",
          sections: [
            {
              id: "overview",
              label: "Overview",
              icon: "gauge",
              intro: "How this server stands and what it is doing right now",
              ...initOverviewSection(section("overview"), { watch, open: (id) => machine.show(id), updates: () => updatesFeed.ask() }),
            },
            { id: "services", label: "Services", icon: "gear", intro: "The services this machine keeps an eye on", ...services },
            { id: "logs", label: "Logs", icon: "rows", intro: "What a service has been saying, and who visited a website", ...logs },
            // The section keeps its id "actions": the log is the actions' audit, and alerts and links name it so.
            { id: "actions", label: "Activity", icon: "activity", intro: "Everything done to this server through the console", ...initActionsSection(section("actions"), watch, output) },
          ],
        },
        {
          id: "websites",
          label: "Websites",
          icon: "globe",
          color: "blue",
          sections: [
            { id: "instances", label: "Instances", icon: "tabs", intro: "The websites this server serves, each with its own build", ...initInstancesSection(section("instances"), watch) },
            { id: "domains", label: "Domains", icon: "link", intro: "Every host as visitors meet it: its DNS, its certificate and its answer", ...initDomainsSection(section("domains"), watch, () => machine.show("instances")) },
            { id: "backups", label: "Backups", icon: "archive", intro: "Copies of the websites and of this machine, and the way back to them", ...initBackupsSection(section("backups"), watch, output, targetDialog) },
          ],
        },
        {
          id: "security",
          label: "Security",
          icon: "shield",
          color: "orange",
          sections: [
            { id: "updates", label: "Updates", icon: "download", intro: "New builds of elpresso machine and of the operating system", ...initUpdatesSection(section("updates"), watch, () => machine.show("instances")) },
            { id: "protection", label: "Protection", icon: "firewall", intro: "The firewall, fail2ban, automatic updates, SSH and the kernel", ...protection },
            {
              id: "security",
              label: "Security",
              icon: "shield-check",
              intro: "What matters on a web server, checked, with why and how to fix it",
              ...initSecuritySection(section("security"), watch, (id) => machine.show(id)),
            },
          ],
        },
        {
          id: "console",
          label: "Console",
          icon: "terminal",
          color: "yellow",
          sections: [{ id: "account", label: "Account", icon: "user", intro: "The account you are logged in with, how long a login lasts, and how the console looks", ...withSession(initAccountSection(section("account"), { roleLabel: () => "operator" }), initSessionCard(query<HTMLFormElement>("[data-session-form]", section("account")))) }],
        },
      ],
      { storageKey: "elpresso.machine.section", navKey: "elpresso.machine.nav" },
    );
    // Whichever section is open, a start and an end are said, and the Activity tab is
    // marked while one runs. The start's toast goes when the end comes: "Refreshing…"
    // beside "Refreshing failed" would say two things at once.
    const startToasts = new Map<number, ToastHandle>();
    watch.subscribe((event) => {
      const shown = event.entry;
      const showOutput = { label: "Show output", run: () => output.open(shown) };
      if (event.kind === "ended" || event.kind === "lost") {
        startToasts.get(shown.id)?.close();
        startToasts.delete(shown.id);
      }
      if (event.kind === "started") startToasts.set(shown.id, showToast(entryTitle(shown), { kind: "info", action: showOutput }));
      else if (event.kind === "ended") {
        const ended = endedToast(shown);
        showToast(ended.title, { kind: ended.kind, detail: ended.detail, action: showOutput });
        // An update or an upgrade that ended changes what waits.
        askAlerts(true);
        if (changedFirewall(shown)) askToKeepFirewall();
      } else if (event.kind === "lost") {
        showToast("The console lost sight of an action", { kind: "warning", detail: `${entryTitle(shown)} Activity says how it went` });
      }
      drawBadges();
    });
    // A firewall's change that nobody keeps is taken back after three minutes: the console
    // asks wherever the operator is, Activity's output included, not only in Protection.
    const askToKeepFirewall = () => {
      void machineApi
        .refreshProtection()
        .then((answer) => {
          protection.take(answer);
          const left = pendingSeconds(answer.report, serverNow());
          if (left === 0) return;
          showToast("Keep the firewall's change?", { kind: "warning", detail: keepFirewallDetail(left), action: { label: "Keep", run: keepFirewall } });
        })
        .catch(() => undefined);
    };
    const keepFirewall = () => {
      void machineApi
        .confirmFirewall()
        .then((answer) => {
          showToast("The firewall's change is kept", { kind: "success" });
          protection.take(answer);
        })
        .catch((error: unknown) => showError(failureText(error), "The firewall's change could not be kept"));
    };
    // The machine looks once a minute; the console asks as often while the tab is visible.
    const askAlerts = (again = false) => {
      if (document.visibilityState !== "visible") return;
      void machineApi
        .alerts()
        .then((answer) => showAlertCounts(answer.alerts))
        .catch(() => undefined);
      void updatesFeed.ask(again).then((answer) => {
        waiting = updatesWaiting(answer.updates, answer.packages);
        drawBadges();
      });
    };
    askAlerts();
    window.setInterval(() => askAlerts(), ALERTS_EVERY);
    document.addEventListener("visibilitychange", () => askAlerts());
    // An action that ran when the page opened is followed from the start.
    void machineApi
      .actions()
      .then((actions) => {
        const running = runningEntry(actions.entries);
        if (running !== null) watch.follow(running);
      })
      .catch(() => undefined);
    root.classList.remove("console--starting");
  })
  .catch((error: unknown) => {
    if (error instanceof ApiRequestError && error.unauthorized) {
      location.replace("/login");
      return;
    }
    // Shown instead of an empty page, e.g. while the machine restarts: the page shows
    // first, so nothing that fails below can leave it blank, and Try again reloads.
    root.classList.remove("console--starting");
    failed.hidden = false;
    failedLine.textContent = failureText(error);
    throw error;
  });
